/**
 * PhotoViewer Component
 * Full-screen photo viewer using yet-another-react-lightbox
 * Replaces custom implementation with robust lightbox library
 */

import { useMemo, useCallback, useEffect, useState } from 'react';
import { Button, Space, Spin, Typography, Divider, Tag } from 'antd';
import { LoadingOutlined, CloseOutlined, InfoCircleOutlined, DownloadOutlined } from '@ant-design/icons';

// YARL imports
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';

// YARL styles - IMPORTANT: always import
import 'yet-another-react-lightbox/styles.css';

import type { Photo } from '@/types';
import { formatDateTimeInDeviceTimezone } from '@/utils/dateUtils';

const { Text } = Typography;

/**
 * Optimized Arrow Icon component - single component for all arrow states
 * Uses glassmorphism effect with backdrop-filter blur
 */
interface ArrowIconProps {
  direction: 'left' | 'right';
  disabled?: boolean;
  size?: number;
}

const ArrowIcon = ({ direction, disabled = false, size = 56 }: ArrowIconProps) => {
  const circleSize = size * 0.7;
  const rotation = direction === 'left' ? '-90deg' : '90deg';
  const fillColor = disabled ? '#888888' : '#ffffff';
  const bgColor = disabled ? 'rgba(80, 80, 80, 0.4)' : 'rgba(128, 128, 128, 0.4)';

  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {/* Blurred circle background - sized to match SVG circle */}
      <div
        style={{
          position: 'absolute',
          width: circleSize,
          height: circleSize,
          borderRadius: '50%',
          background: bgColor,
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
        }}
      />
      {/* SVG arrow icon on top */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height={size}
        width={size}
        viewBox="0 0 20 20"
        fill={fillColor}
        style={{
          transform: `rotate(${rotation})`,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <g>
          <rect fill="none" height="20" width="20" />
          <path d="M10,4c3.31,0,6,2.69,6,6s-2.69,6-6,6s-6-2.69-6-6S6.69,4,10,4 M10,3c-3.87,0-7,3.13-7,7c0,3.87,3.13,7,7,7 c3.87,0,7-3.13,7-7C17,6.13,13.87,3,10,3L10,3z M9.5,10v3h1v-3H13l-3-3l-3,3H9.5z" />
        </g>
      </svg>
    </div>
  );
};

export interface PhotoViewerProps {
  // Photo data
  photo: Photo;
  photos?: Photo[]; // Optional: Enable navigation if provided
  currentIndex?: number; // Required if photos provided

  // Display options
  showFullMetadata?: boolean; // Default: false (kept for API compatibility)
  initialLoading?: boolean; // Default: false (show spinner while fetching photo data)
  loadingMessage?: string; // Custom loading message (default: "Loading photo...")
  timezone?: string; // Device timezone (IANA format, e.g. "Europe/Rome")

  // Actions (callbacks)
  onClose: () => void; // Required
  onNavigate?: (index: number) => void; // Required if photos provided
  onShare?: () => void; // Optional: Show share button if provided
  onDownload?: () => void; // Optional: Show download button if provided
  extraActions?: React.ReactNode; // Optional: Custom toolbar actions
}

// A slide still loading after this long is considered stalled and re-requested.
// Generous on purpose: a retry restarts a ~6 MB original from zero.
const STALL_TIMEOUT_MS = 20_000;
// Re-requests per photo before giving up (each one bypasses the CDN cache)
const MAX_STALL_RETRIES = 3;

/**
 * Append a cache-busting query param. The Cloudflare edge cache in front of R2
 * occasionally serves a 200 whose body never arrives (or ends in
 * ERR_HTTP2_PROTOCOL_ERROR); a distinct URL skips that cache entry and is
 * fetched from R2 directly. R2 ignores the query string.
 */
const withRetry = (url: string, retry: number) =>
  retry > 0 ? `${url}${url.includes('?') ? '&' : '?'}r=${retry}` : url;

/**
 * Convert Photo to YARL Slide format
 */
const photoToSlide = (photo: Photo, retry: number) => {
  return {
    src: photo.urls?.full ? withRetry(photo.urls.full, retry) : '',
  };
};

/**
 * Format timestamp for display using device timezone
 */
const formatTimestamp = (timestamp: any, timezone?: string) => {
  if (!timestamp) return 'Unknown';
  const formatted = formatDateTimeInDeviceTimezone(timestamp, timezone);
  return formatted || 'Invalid date';
};

/**
 * Format EV compensation value
 */
const formatEV = (ev: number | undefined) => {
  if (ev === undefined || ev === 0) return 'EV 0';
  return ev > 0 ? `EV +${ev}` : `EV ${ev}`;
};

/**
 * Format camera lens name
 */
const formatCamera = (camera: string | undefined) => {
  if (!camera) return '—';
  return camera.charAt(0).toUpperCase() + camera.slice(1);
};

/**
 * Format focus mode
 */
const formatFocus = (focus: string | undefined) => {
  if (!focus) return '—';
  return focus === 'auto' ? 'AF' : '∞';
};

/**
 * Format HDR mode
 */
const formatHDR = (hdr: string | undefined) => {
  if (!hdr || hdr === 'off') return 'HDR off';
  return `HDR ${hdr}`;
};

export function PhotoViewer({
  photo,
  photos,
  currentIndex = 0,
  showFullMetadata: _showFullMetadata = false, // Kept for API compatibility
  initialLoading = false,
  loadingMessage = 'Loading photo...',
  timezone,
  onClose,
  onNavigate,
  onShare: _onShare, // Not used in YARL UI
  onDownload: _onDownload, // Not used in YARL UI
  extraActions,
}: PhotoViewerProps) {
  // Metadata panel visibility state (hidden by default)
  const [metadataVisible, setMetadataVisible] = useState(false);

  // Download current photo via blob fetch (cross-origin URLs don't support <a download>)
  const handleDownload = useCallback(async () => {
    const target = photos && photos.length > 0 ? photos[currentIndex] : photo;
    const url = target?.urls?.full;
    if (!url) return;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `photo-${target.photoId || 'download'}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback: open in new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [photos, currentIndex, photo]);

  // Cache-busting retry count per photoId (see withRetry)
  const [retries, setRetries] = useState<Record<string, number>>({});

  // Convert photos to YARL slides
  const slides = useMemo(() => {
    if (photos && photos.length > 0) {
      return photos.map((p) => photoToSlide(p, retries[p.photoId] || 0));
    }
    return [photoToSlide(photo, retries[photo.photoId] || 0)];
  }, [photos, photo, retries]);

  // Image URLs that finished loading in this viewer session
  const [loadedSrcs, setLoadedSrcs] = useState<Set<string>>(() => new Set());

  const watchedPhoto = photos && photos.length > 0 ? photos[currentIndex] : photo;
  const watchedId = watchedPhoto?.photoId;
  const watchedRetry = watchedId ? retries[watchedId] || 0 : 0;
  const activeSrc = slides[photos && photos.length > 0 ? currentIndex : 0]?.src || '';
  const activeLoaded = loadedSrcs.has(activeSrc);

  // Track slide image load/error. These events do not bubble, so listen in the
  // capture phase on the document, where every YARL <img> is reachable.
  useEffect(() => {
    const isSlideImage = (t: EventTarget | null): t is HTMLImageElement =>
      t instanceof HTMLImageElement && t.classList.contains('yarl__slide_image');

    const onLoad = (e: Event) => {
      if (!isSlideImage(e.target)) return;
      const src = e.target.getAttribute('src');
      if (src) setLoadedSrcs((prev) => (prev.has(src) ? prev : new Set(prev).add(src)));
    };
    // A failed visible photo (e.g. ERR_HTTP2_PROTOCOL_ERROR) is re-requested at once
    const onError = (e: Event) => {
      if (!isSlideImage(e.target) || !e.target.closest('.yarl__slide_current')) return;
      if (!watchedId || watchedRetry >= MAX_STALL_RETRIES) return;
      console.warn(`[PhotoViewer] ${watchedId} failed, retry ${watchedRetry + 1}`);
      setRetries((prev) => ({ ...prev, [watchedId]: watchedRetry + 1 }));
    };

    document.addEventListener('load', onLoad, true);
    document.addEventListener('error', onError, true);
    return () => {
      document.removeEventListener('load', onLoad, true);
      document.removeEventListener('error', onError, true);
    };
  }, [watchedId, watchedRetry]);

  // Stall watchdog: YARL has no load timeout, so a response whose body never
  // arrives would spin forever. Re-request it under a cache-busting URL.
  useEffect(() => {
    if (!watchedId || activeLoaded || watchedRetry >= MAX_STALL_RETRIES) return;
    const timer = window.setTimeout(() => {
      console.warn(`[PhotoViewer] ${watchedId} stalled, retry ${watchedRetry + 1}`);
      setRetries((prev) => ({ ...prev, [watchedId]: watchedRetry + 1 }));
    }, STALL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [watchedId, watchedRetry, activeLoaded]);

  // Handle slide change (navigation)
  const handleViewChange = useCallback(
    ({ index }: { index: number }) => {
      if (onNavigate && index !== currentIndex) {
        onNavigate(index);
      }
    },
    [onNavigate, currentIndex]
  );

  // Get current photo for metadata display
  const currentPhoto = photos && photos.length > 0 ? photos[currentIndex] : photo;

  // Render metadata panel
  const renderMetadataPanel = () => {
    if (!metadataVisible) return null;

    const { metadata, settings, exif } = currentPhoto;
    const width = exif?.width || metadata?.width;
    const height = exif?.height || metadata?.height;

    // Tag style matching ViewCard overlay tags
    const overlayTagStyle = { backgroundColor: 'rgba(0, 0, 0, 0.3)', border: 'none', color: 'white' };

    return (
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0, 0, 0, 0.2)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          padding: '12px 16px',
          borderRadius: 12,
          maxWidth: '90vw',
          zIndex: 10010,
        }}
      >
        <Space direction="vertical" size="small" align="center">
          {/* Resolution tag */}
          {width && height && (
            <Tag className="text-xs px-2 py-0 leading-tight m-0" style={overlayTagStyle}>
              {width} × {height}
            </Tag>
          )}

          {/* Captured timestamp */}
          <Text style={{ color: '#ccc', fontSize: 12 }}>
            {formatTimestamp(currentPhoto.capturedAt, timezone)}
          </Text>

          {/* EXIF and camera settings */}
          {(settings || exif) && (
            <>
              <Divider style={{ margin: '4px 0', borderColor: 'rgba(255,255,255,0.2)' }} />
              {/* Line 1: ISO · Shutter · WB */}
              <Tag className="text-xs px-2 py-0 leading-tight m-0" style={overlayTagStyle}>
                {exif?.iso ? `ISO ${exif.iso}` : '—'}
                {' · '}
                {exif?.shutterSpeed || '—'}
                {' · '}
                {exif?.whiteBalance ? `WB ${exif.whiteBalance}` : '—'}
              </Tag>
              {/* Line 2: Camera · Focus · EV · HDR */}
              <Tag className="text-xs px-2 py-0 leading-tight m-0" style={overlayTagStyle}>
                {formatCamera(settings?.camera)}
                {' · '}
                {formatFocus(settings?.focusMode)}
                {' · '}
                {formatEV(settings?.evCompensation)}
                {' · '}
                {formatHDR(settings?.hdrMode)}
              </Tag>
            </>
          )}
        </Space>
      </div>
    );
  };

  // If in initial loading state, show a loading overlay (dark theme)
  if (initialLoading) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10007,
          backgroundColor: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Space direction="vertical" align="center">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#ffffff' }} spin />} />
          <Text style={{ color: '#ffffff' }}>{loadingMessage}</Text>
        </Space>
        <Button
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: '#ffffff',
          }}
          icon={<CloseOutlined style={{ color: '#ffffff' }} />}
          onClick={onClose}
          type="text"
        >
          Close
        </Button>
        {extraActions && <div style={{ position: 'absolute', top: 16, left: 16 }}>{extraActions}</div>}
      </div>
    );
  }

  // Check if we have valid image URLs
  const hasValidUrl = photo.urls?.full || (photos && photos.some((p) => p.urls?.full));

  // If no valid URLs, show error state (dark theme)
  if (!hasValidUrl) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10007,
          backgroundColor: '#000',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 16,
            borderBottom: '1px solid #333',
          }}
        >
          <Button
            icon={<CloseOutlined style={{ color: '#ffffff' }} />}
            onClick={onClose}
            type="text"
            style={{ color: '#ffffff' }}
          >
            Close
          </Button>
          {extraActions && <Space>{extraActions}</Space>}
        </div>

        {/* Error content */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Text style={{ color: '#ffffff', fontSize: 20, marginBottom: 16 }}>No image URL available</Text>
            <br />
            <div
              style={{
                marginTop: 16,
                textAlign: 'left',
                background: '#1a1a1a',
                padding: 16,
                borderRadius: 8,
                maxWidth: 600,
                margin: '16px auto',
              }}
            >
              <Text
                style={{
                  color: '#999',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {JSON.stringify(photo, null, 2)}
              </Text>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalPhotos = photos?.length || 1;

  return (
    <>
      <Lightbox
        open={true}
        close={onClose}
        slides={slides}
        index={currentIndex}
        on={{
          view: handleViewChange,
        }}
        plugins={[Zoom]}
        zoom={{
          maxZoomPixelRatio: 3,
          scrollToZoom: true,
        }}
        styles={{
          container: { backgroundColor: '#000' },
        }}
        controller={{
          closeOnBackdropClick: true,
        }}
        carousel={{
          finite: true, // Always finite - show disabled arrows at boundaries
          // Originals are ~6 MB: preload one neighbour per side (default 2) so the
          // visible photo does not share bandwidth with four others
          preload: 1,
        }}
        render={{
          // Custom arrow icons - circle with arrow and blurred background
          // At boundaries, show grayed out version of same arrow design
          iconPrev: () => {
            const isAtStart = currentIndex === 0;
            return <ArrowIcon direction="left" disabled={isAtStart} />;
          },
          iconNext: () => {
            const isAtEnd = currentIndex >= totalPhotos - 1;
            return <ArrowIcon direction="right" disabled={isAtEnd} />;
          },
          // Sequential loading: neighbours get no <img> (hence no request) until the
          // active photo has loaded, so it never shares bandwidth with them.
          // Neighbours already loaded stay rendered to keep swipe animations intact.
          slide: ({ slide, offset }) =>
            offset !== 0 && !activeLoaded && !loadedSrcs.has((slide as { src?: string }).src || '') ? (
              <div />
            ) : undefined,
          iconLoading: () => <LoadingOutlined style={{ fontSize: 48, color: '#ffffff' }} spin />,
          // Custom close icon - rendered inside YARL for proper z-index
          iconClose: () => <CloseOutlined style={{ fontSize: 20, color: '#ffffff' }} />,
          // Hide zoom toolbar buttons (scroll-to-zoom still works)
          buttonZoom: () => null,
        }}
        toolbar={{
          buttons: [
            // Download button
            <button
              key="download"
              type="button"
              className="yarl__button"
              aria-label="Download"
              onClick={handleDownload}
              style={{
                background: 'transparent',
                border: 'none',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <DownloadOutlined style={{ fontSize: 20, color: '#ffffff' }} />
            </button>,
            // Info button for metadata toggle
            <button
              key="info"
              type="button"
              className="yarl__button"
              aria-label="Info"
              onClick={() => setMetadataVisible(!metadataVisible)}
              style={{
                background: metadataVisible ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                border: 'none',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                marginRight: 8,
              }}
            >
              <InfoCircleOutlined style={{ fontSize: 20, color: '#ffffff' }} />
            </button>,
            'close',
          ],
        }}
      />
      {/* Photo counter and RAW badge overlay - top left */}
      <div
        style={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 10010,
          display: 'flex',
          gap: 8,
        }}
      >
        {totalPhotos > 1 && (
          <Tag
            className="text-xs px-2 py-0 leading-tight m-0"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', border: 'none', color: 'white' }}
          >
            {currentIndex + 1} / {totalPhotos}
          </Tag>
        )}
        {currentPhoto.rawUrl && (
          <Tag
            className="text-xs px-2 py-0 leading-tight m-0"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', border: 'none', color: 'white' }}
          >
            RAW
          </Tag>
        )}
      </div>
      {/* Metadata panel overlay */}
      {renderMetadataPanel()}
    </>
  );
}
